import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { PanelFrame } from '../workspace/PanelFrame';
import { LineChart as LineChartIcon, Zap, Loader2, Layers, Flame, GitBranch, RefreshCw, Map as MapIcon } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ReferenceArea, Legend,
} from 'recharts';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// ── Shared chart constants ───────────────────────────────────────────────────
const CHART_MARGIN        = { top: 10, right: 12, left: -10, bottom: 0 };
const TICK_STYLE          = { fontSize: 10, fill: 'hsl(var(--muted-foreground))' };
const AXIS_STYLE          = { stroke: 'hsl(var(--border))' };
const X_LABEL_STYLE       = { value: 'Chapter', position: 'insideBottom', offset: -2, style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } };
const TOOLTIP_STYLE       = { background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 6, fontSize: 11 };
const TOOLTIP_LABEL_STYLE = { color: 'hsl(var(--muted-foreground))' };
const DOT                 = { r: 2.5 };
const ACTIVE_DOT          = { r: 5 };
const Y_DOMAIN            = [0, 100];

// 12 visually distinct colours — used by index so every character gets a
// unique line regardless of the `character.color` field (which defaults to
// "chart-1" for all characters when bulk-generated, causing invisible toggling).
const CHAR_COLORS = [
  '#e879f9', '#38bdf8', '#34d399', '#fbbf24', '#f87171',
  '#a78bfa', '#fb923c', '#4ade80', '#60a5fa', '#f472b6',
  '#94a3b8', '#c084fc', '#2dd4bf',
];
function charColor(index) { return CHAR_COLORS[index % CHAR_COLORS.length]; }

// Dimension palette — each dimension gets a fixed colour
const LAYER_META = {
  internal:   { label: 'Internal',   color: '#a78bfa' },
  external:   { label: 'External',   color: '#f87171' },
  relational: { label: 'Relational', color: '#34d399' },
  moral:      { label: 'Moral',      color: '#fbbf24' },
};
const LAYER_KEYS = ['internal', 'external', 'relational', 'moral'];

// ── Tab toggle ───────────────────────────────────────────────────────────────
function TabBar({ active, onChange }) {
  return (
    <div className="flex shrink-0 gap-0 overflow-x-auto border-b border-panel-border">
      {[
        { id: 'overview',   icon: LineChartIcon, label: 'Arc Overview' },
        { id: 'layers',     icon: Layers,        label: 'Layer Breakdown' },
        { id: 'tension',    icon: Flame,         label: 'Tension' },
        { id: 'continuity', icon: GitBranch,     label: 'Continuity' },
        { id: 'storymap',   icon: MapIcon,       label: 'Story Map' },
      ].map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          data-testid={`arc-tab-${id}`}
          onClick={() => onChange(id)}
          className={cn(
            'flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-[13.5px] font-medium transition-colors',
            active === id
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground',
          )}
        >
          <Icon className="h-3.5 w-3.5" />
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Shared empty state ───────────────────────────────────────────────────────
function EmptyState({ icon: Icon, title, body, onGenerate, generating, label }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
      <Icon className="h-10 w-10 text-muted-foreground/40" />
      <div>
        <p className="text-[13px] font-semibold">{title}</p>
        <p className="mt-1 max-w-[280px] text-[13.5px] leading-snug text-muted-foreground">{body}</p>
      </div>
      <Button variant="default" size="sm" className="gap-1.5"
              onClick={onGenerate} disabled={generating} data-testid="arc-generate-empty-btn">
        {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
        {generating ? 'Generating…' : label}
      </Button>
    </div>
  );
}

// ── Stats row ────────────────────────────────────────────────────────────────
function Stat({ label, value, sub, tone = 'primary' }) {
  return (
    <div className="flex flex-col border-r border-panel-border px-3 py-2 last:border-r-0">
      <span className="text-[13px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className={cn('text-[14px] font-semibold font-mono', `text-${tone}`)}>{value}</span>
      {sub && <span className="text-[13px] text-muted-foreground">{sub}</span>}
    </div>
  );
}

// ── Tab 1 — Arc Overview ─────────────────────────────────────────────────────
// ── Convergence-aware custom Tooltip ─────────────────────────────────────────
function ArcTooltip({ active, payload, label, stats }) {
  if (!active || !payload?.length) return null;
  const isConv = stats.bestChNumber != null && String(stats.bestChNumber) === String(label);
  let closestPair = null;
  if (isConv && stats.convergenceCharScores) {
    const entries = Object.entries(stats.convergenceCharScores).sort((a, b) => a[1] - b[1]);
    if (entries.length >= 2) {
      let minDiff = Infinity, pA = '', pB = '';
      for (let i = 0; i < entries.length - 1; i++) {
        const diff = entries[i + 1][1] - entries[i][1];
        if (diff < minDiff) { minDiff = diff; pA = entries[i][0]; pB = entries[i + 1][0]; }
      }
      closestPair = { a: pA, b: pB, diff: Math.round(minDiff) };
    }
  }
  return (
    <div data-testid="arc-convergence-tooltip" style={{
      background: 'hsl(var(--popover))',
      border: `1px solid ${isConv ? '#f59e0b' : 'hsl(var(--border))'}`,
      borderRadius: 6, padding: '8px 10px', fontSize: 11,
      boxShadow: isConv ? '0 0 0 2px #fef3c720' : undefined,
      minWidth: 140,
    }}>
      <p style={{ fontWeight: 700, color: 'hsl(var(--foreground))', marginBottom: 5 }}>
        {isConv ? '\u21d0 ' : ''}Chapter {label}
      </p>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: p.color, marginBottom: 2 }}>
          <span style={{ fontWeight: 500 }}>{p.dataKey}</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
      {isConv && (
        <div style={{ marginTop: 7, paddingTop: 6, borderTop: '1px solid #f59e0b50' }}>
          <p style={{ color: '#f59e0b', fontWeight: 700, fontSize: 10, marginBottom: 3 }}>
            Convergence point
          </p>
          {stats.convergenceDeviation != null && (
            <p style={{ color: '#d97706', fontSize: 10, marginBottom: 2 }}>
              Deviation score: <strong>{stats.convergenceDeviation}</strong>
            </p>
          )}
          {closestPair && (
            <p style={{ color: '#d97706', fontSize: 10 }}>
              Closest: {closestPair.a} &amp; {closestPair.b} &mdash; {closestPair.diff} pts apart
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function OverviewTab({ characters, chapters, project, setCharacters }) {
  const storageKey = project?.id ? `arc-active-ids-${project.id}` : null;

  // Default: no characters selected (empty set). Restore persisted selection
  // from localStorage so the user's last choice survives panel close/reopen.
  const [activeIds, setActiveIds] = useState(() => {
    if (!storageKey) return [];
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [generating, setGenerating] = useState(false);

  // Persist selection to localStorage whenever the user changes it.
  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(activeIds)); } catch { /* non-fatal */ }
  }, [activeIds, storageKey]);

  const hasArcData = useMemo(
    () => characters.some(c => Array.isArray(c.arc) && c.arc.length > 0),
    [characters],
  );

  const data = useMemo(() =>
    chapters.map((c, i) => {
      const row = { ch: `${c.number}` };
      characters.forEach(ch => {
        const arc = Array.isArray(ch.arc) ? ch.arc : [];
        row[ch.name] = typeof arc[i] === 'number' ? arc[i] : null;
      });
      return row;
    }),
    [characters, chapters],
  );

  const visibleCharacters = useMemo(
    () => characters.filter(c => activeIds.includes(c.id)),
    [characters, activeIds],
  );

  const handleGenerate = useCallback(async () => {
    if (!project?.id) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/projects/${project.id}/generate-arc-scores`, {}, { withCredentials: true, timeout: 90000 });
      const updated = r.data?.characters || [];
      if (updated.length) {
        setCharacters(prev => prev.map(c => {
          const found = updated.find(u => u.id === c.id);
          return found ? { ...c, arc: found.arc } : c;
        }));
        toast.success(`Arc scores generated for ${updated.length} character${updated.length === 1 ? '' : 's'}.`);
      } else {
        toast.warning('No arc data returned — make sure chapters have content.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Arc generation failed.');
    } finally {
      setGenerating(false);
    }
  }, [project?.id, setCharacters]);

  // Summary stats
  const stats = useMemo(() => {
    if (!visibleCharacters.length || !chapters.length) {
      return { avgGrowth: '—', lowest: '—', lowestSub: '', convergence: '—', convergenceSub: '' };
    }
    let sumGrowth = 0, growthCount = 0, lowestName = null, lowestDelta = Infinity,
        bestCh = null, bestVar = Infinity, bestChIdx = -1;
    visibleCharacters.forEach(c => {
      const arc = (Array.isArray(c.arc) ? c.arc : []).filter(n => typeof n === 'number');
      if (arc.length >= 2) {
        const delta = arc[arc.length - 1] - arc[0];
        sumGrowth += delta; growthCount++;
        if (delta < lowestDelta) { lowestDelta = delta; lowestName = c.name.split(' ')[0]; }
      }
    });
    for (let i = 0; i < chapters.length; i++) {
      const vals = visibleCharacters.map(c => Array.isArray(c.arc) ? c.arc[i] : null).filter(v => typeof v === 'number');
      if (vals.length < 2) continue;
      const mean = vals.reduce((s, x) => s + x, 0) / vals.length;
      const varc = vals.reduce((s, x) => s + Math.abs(x - mean), 0);
      if (varc < bestVar) { bestVar = varc; bestCh = chapters[i]?.number; bestChIdx = i; }
    }
    // Character scores at convergence chapter
    let convergenceCharScores = null;
    if (bestChIdx >= 0) {
      convergenceCharScores = {};
      visibleCharacters.forEach(c => {
        const v = Array.isArray(c.arc) ? c.arc[bestChIdx] : null;
        if (typeof v === 'number') convergenceCharScores[c.name.split(' ')[0]] = v;
      });
    }
    return {
      avgGrowth: growthCount ? (sumGrowth / growthCount >= 0 ? '+' : '') + Math.round(sumGrowth / growthCount) : '—',
      lowest: lowestName || '—',
      lowestSub: lowestName && Number.isFinite(lowestDelta) ? `${lowestDelta >= 0 ? '+' : ''}${lowestDelta} pts` : '',
      convergence: bestCh ? `Ch ${bestCh}` : '—',
      convergenceSub: bestCh ? 'tightest arc' : '',
      bestChNumber: bestCh,
      convergenceDeviation: bestChIdx >= 0 ? Math.round(bestVar * 10) / 10 : null,
      convergenceCharScores,
    };
  }, [visibleCharacters, chapters]);

  return (
    <>
      {/* Character toggle bar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-panel-border bg-panel-header/40 px-2 py-2">
        {characters.map((c, idx) => {
          const on = activeIds.includes(c.id);
          const color = charColor(idx);
          return (
            <button key={c.id} onClick={() => setActiveIds(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                    data-testid={`arc-char-btn-${c.id}`} aria-pressed={on}
                    className={cn('flex items-center gap-1.5 rounded-sm border border-border bg-background px-1.5 py-1 text-[13px] transition-colors', on ? 'opacity-100' : 'opacity-30')}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
              <span className="font-medium">{c.name.split(' ')[0]}</span>
              <span className="text-muted-foreground">{c.role}</span>
            </button>
          );
        })}
        <Button variant="outline" size="sm" className="ml-auto h-7 gap-1.5 px-2 text-[13px]"
                onClick={handleGenerate} disabled={generating || !project?.id || !characters.length}
                data-testid="arc-generate-btn" title="Score each character's presence per chapter by name-mention frequency (instant, no AI cost)">
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
          {generating ? 'Scoring…' : hasArcData ? 'Refresh Scores' : 'Score Presence'}
        </Button>
      </div>

      {/* Chart or empty state */}
      {hasArcData ? (
        <div className="flex-1 p-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={CHART_MARGIN}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="ch" tick={TICK_STYLE} axisLine={AXIS_STYLE} tickLine={false} label={X_LABEL_STYLE} />
              <YAxis domain={Y_DOMAIN} tick={TICK_STYLE} axisLine={AXIS_STYLE} tickLine={false} />
              <Tooltip content={(props) => <ArcTooltip {...props} stats={stats} />} />
              <Legend wrapperStyle={{ fontSize: 10, paddingTop: 2 }} />
              <ReferenceLine y={50} stroke="hsl(var(--border))" strokeDasharray="3 3" />
              {/* Convergence annotation — vertical line at the chapter where arcs are tightest */}
              {stats.bestChNumber != null && (
                <ReferenceLine
                  x={String(stats.bestChNumber)}
                  stroke="#f59e0b"
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  label={{
                    value: 'convergence',
                    position: 'insideTopLeft',
                    style: { fontSize: 9, fill: '#f59e0b', fontWeight: 600 },
                    offset: 4,
                  }}
                />
              )}
              {visibleCharacters.map(c => (
                <Line key={c.id} type="monotone" dataKey={c.name}
                      stroke={charColor(characters.indexOf(c))}
                      strokeWidth={2} dot={DOT} activeDot={ACTIVE_DOT}
                      connectNulls isAnimationActive={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyState icon={LineChartIcon} title="No arc data yet"
          body="Click Score Presence — instantly counts how often each character appears per chapter and plots it as a 0–100 presence arc."
          onGenerate={handleGenerate} generating={generating} label="Score Presence" />
      )}

      {/* Stats row */}
      <div className="shrink-0 grid grid-cols-3 border-t border-panel-border">
        <Stat label="Avg. Growth"  value={stats.avgGrowth}   tone="success"     />
        <Stat label="Lowest Arc"   value={stats.lowest}      tone="destructive" sub={stats.lowestSub}      />
        <Stat label="Convergence"  value={stats.convergence} tone="primary"     sub={stats.convergenceSub} />
      </div>
    </>
  );
}

// Narrative label generators per dimension
const DIM_NARRATIVE = {
  internal:   (peak, ch) => `Psychological peak Ch ${ch} — deepest inner conflict or self-discovery`,
  external:   (peak, ch) => `Action peak Ch ${ch} — most plot-active moment`,
  relational: (peak, ch) => `Relationship peak Ch ${ch} — strongest interpersonal shift`,
  moral:      (peak, ch) => `Moral peak Ch ${ch} — ethics most tested, highest values tension`,
};
const DIM_DESCRIPTION = {
  internal:   'Psychological growth, inner conflict, beliefs & self-discovery',
  external:   'Physical actions, plot involvement, external conflict',
  relational: 'Interpersonal dynamics, relationship change & loyalty',
  moral:      'Ethical dilemmas, values tested, moral growth or decline',
};
// ── Tab 2 — Layer Breakdown ───────────────────────────────────────────────────
function LayersTab({ characters, chapters, project, setCharacters }) {
  const [selectedCharId, setSelectedCharId] = useState(() => characters[0]?.id || null);
  const [generating, setGenerating] = useState(false);

  // Auto-select first character if none selected yet
  useEffect(() => {
    if (!selectedCharId && characters.length) setSelectedCharId(characters[0].id);
  }, [characters, selectedCharId]);

  const selectedChar = useMemo(
    () => characters.find(c => c.id === selectedCharId) || null,
    [characters, selectedCharId],
  );

  const hasLayerData = useMemo(
    () => characters.some(c => c.arc_layers && Object.keys(c.arc_layers).length > 0),
    [characters],
  );

  const layerData = useMemo(() => {
    if (!selectedChar?.arc_layers) return [];
    return chapters.map((ch, i) => {
      const row = { ch: `${ch.number}` };
      for (const dim of LAYER_KEYS) {
        const arr = selectedChar.arc_layers[dim] || [];
        row[dim] = typeof arr[i] === 'number' ? arr[i] : null;
      }
      return row;
    });
  }, [selectedChar, chapters]);

  const handleGenerate = useCallback(async () => {
    if (!project?.id) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/projects/${project.id}/generate-arc-layers`, {}, { withCredentials: true, timeout: 90000 });
      const updated = r.data?.characters || [];
      if (updated.length) {
        setCharacters(prev => prev.map(c => {
          const found = updated.find(u => u.id === c.id);
          return found ? { ...c, arc_layers: found.arc_layers } : c;
        }));
        toast.success(`Layer scores generated for ${updated.length} character${updated.length === 1 ? '' : 's'}.`);
      } else {
        toast.warning('No layer data returned — make sure chapters have content.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Layer generation failed.');
    } finally {
      setGenerating(false);
    }
  }, [project?.id, setCharacters]);

  // Per-dimension peak stats for selected character
  const dimStats = useMemo(() => {
    if (!selectedChar?.arc_layers || !chapters.length) return null;
    const result = {};
    for (const dim of LAYER_KEYS) {
      const vals = (selectedChar.arc_layers[dim] || []).filter(v => typeof v === 'number');
      const peak = vals.length ? Math.max(...vals) : 0;
      const peakIdx = vals.lastIndexOf(peak);
      result[dim] = { peak, peakChapter: chapters[peakIdx]?.number };
    }
    return result;
  }, [selectedChar, chapters]);

  return (
    <>
      {/* Controls bar */}
      <div className="flex items-center gap-2 border-b border-panel-border bg-panel-header/40 px-2 py-2">
        <span className="text-[13px] text-muted-foreground">Character:</span>
        <Select value={selectedCharId || ''} onValueChange={setSelectedCharId}>
          <SelectTrigger data-testid="arc-layer-char-select" className="h-7 w-44 text-[13px]">
            <SelectValue placeholder="Pick a character…" />
          </SelectTrigger>
          <SelectContent>
            {characters.map(c => (
              <SelectItem key={c.id} value={c.id} className="text-[13px]">{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="ml-auto h-7 gap-1.5 px-2 text-[13px]"
                onClick={handleGenerate} disabled={generating || !project?.id || !characters.length}
                data-testid="arc-layers-generate-btn">
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
          {generating ? 'Generating…' : hasLayerData ? 'Regenerate Layers' : 'Generate Layers'}
        </Button>
      </div>

      {/* Chart or empty state */}
      {hasLayerData && selectedChar?.arc_layers ? (
        <div className="flex-1 p-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={layerData} margin={CHART_MARGIN}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="ch" tick={TICK_STYLE} axisLine={AXIS_STYLE} tickLine={false} label={X_LABEL_STYLE} />
              <YAxis domain={Y_DOMAIN} tick={TICK_STYLE} axisLine={AXIS_STYLE} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} />
              <ReferenceLine y={50} stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
              {LAYER_KEYS.map(dim => (
                <Line key={dim} type="monotone" dataKey={dim} name={LAYER_META[dim].label}
                      stroke={LAYER_META[dim].color} strokeWidth={2}
                      dot={DOT} activeDot={ACTIVE_DOT} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyState icon={Layers} title="No layer data yet"
          body="Click Generate Layers — Claude will score each character's Internal, External, Relational, and Moral arc per chapter."
          onGenerate={handleGenerate} generating={generating} label="Generate Layers" />
      )}

      {/* Dimension peak stats */}
      {dimStats && (
        <>
          {/* Narrative interpretation — plain-English peak summary */}
          <div className="shrink-0 border-t border-panel-border bg-panel-header/30 px-3 py-2 space-y-1"
               data-testid="arc-narrative-peaks">
            <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
              Narrative peak moments — {selectedChar?.name?.split(' ')[0]}
            </p>
            {LAYER_KEYS.map(dim => {
              const { peak, peakChapter } = dimStats[dim] || {};
              if (peakChapter == null) return null;
              return (
                <div key={dim} className="flex items-start gap-2">
                  <span
                    className="mt-0.5 h-2 w-2 rounded-full shrink-0"
                    style={{ background: LAYER_META[dim].color }}
                  />
                  <p className="text-[13px] leading-snug text-foreground/80">
                    {DIM_NARRATIVE[dim](peak, peakChapter)}
                  </p>
                </div>
              );
            })}
          </div>
          {/* Numeric peak grid */}
          <div className="shrink-0 grid grid-cols-4 border-t border-panel-border">
            {LAYER_KEYS.map(dim => (
              <div key={dim} className="flex flex-col border-r border-panel-border px-2 py-1.5 last:border-r-0"
                   title={DIM_DESCRIPTION[dim]}>
                <span className="text-[13px] uppercase tracking-wider text-muted-foreground">{dim}</span>
                <span className="text-[13px] font-semibold" style={{ color: LAYER_META[dim].color }}>
                  {dimStats[dim]?.peak ?? '—'}
                </span>
                {dimStats[dim]?.peakChapter != null && (
                  <span className="text-[13px] text-muted-foreground">peak Ch {dimStats[dim].peakChapter}</span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ── Tab 3 — Tension Heatmap ───────────────────────────────────────────────────
// Maps pacing / hook_strength (0-100) per chapter onto a colour grid +
// bar chart. Data comes from `project.plot_extraction.chapter_beats`, which
// is populated by the Plot Builder. No API call needed.

function tensionColor(v) {
  if (v == null || v === undefined) return 'hsl(var(--muted))';
  if (v <= 30) return '#60a5fa';                        // calm — blue
  if (v <= 55) return '#fbbf24';                        // rising — amber
  if (v <= 75) return 'hsl(var(--primary))';            // tense — brand orange
  return '#ef4444';                                     // peak — red
}
function tensionLabel(v) {
  if (v == null) return 'No data';
  if (v <= 30)  return 'Calm';
  if (v <= 55)  return 'Rising';
  if (v <= 75)  return 'Tense';
  return 'Peak';
}

function TensionTab({ chapters, project }) {
  const chapterBeats = project?.plot_extraction?.chapter_beats || [];

  const tensionData = useMemo(() => {
    if (!chapters.length) return [];
    return chapters.map((ch) => {
      const beat = chapterBeats.find((b) => b.chapter_number === ch.number);
      const tension = typeof beat?.hook_strength === 'number' ? beat.hook_strength : null;
      return {
        chapter: ch.number,
        name: `Ch ${ch.number}`,
        title: ch.title || `Chapter ${ch.number}`,
        tension,
      };
    });
  }, [chapters, chapterBeats]);

  const hasData = tensionData.some((d) => d.tension !== null);

  if (!chapters.length || !hasData) {
    return (
      <EmptyState
        icon={Flame}
        title="No tension data yet"
        body="Generate your Plot Blueprint in the Plot Builder first. The pacing beats set hook-strength scores per chapter that power this chart."
        onGenerate={() => window.dispatchEvent(new CustomEvent('loom:open-plot-builder'))}
        generating={false}
        label="Open Plot Builder"
      />
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        {[
          { color: '#60a5fa', label: 'Calm (0–30)' },
          { color: '#fbbf24', label: 'Rising (31–55)' },
          { color: 'hsl(var(--primary))', label: 'Tense (56–75)' },
          { color: '#ef4444', label: 'Peak (76–100)' },
        ].map(({ color, label }) => (
          <span key={label} className="flex items-center gap-1">
            <span className="inline-block h-2 w-4 rounded-sm" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>

      {/* Heatmap grid — one cell per chapter */}
      <div className="flex flex-wrap gap-1.5" data-testid="tension-heatmap-grid">
        {tensionData.map((d) => (
          <div
            key={d.chapter}
            data-testid={`tension-cell-ch-${d.chapter}`}
            className="group relative flex h-10 w-10 cursor-default items-center justify-center rounded text-[10px] font-bold text-white shadow-sm transition-transform hover:scale-110"
            style={{ background: tensionColor(d.tension) }}
            title={`${d.title}\nTension: ${d.tension ?? '—'} · ${tensionLabel(d.tension)}`}
          >
            {d.chapter}
            {d.tension != null && (
              <span className="absolute -bottom-5 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded bg-popover px-1 py-0.5 text-[9px] text-popover-foreground shadow group-hover:block border border-border">
                {d.tension}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Bar chart for trend overview */}
      <div className="h-44 shrink-0" data-testid="tension-bar-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={tensionData} margin={CHART_MARGIN} barSize={tensionData.length > 30 ? 6 : 14}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="name" tick={TICK_STYLE} axisLine={AXIS_STYLE} tickLine={false}
              interval={Math.ceil(tensionData.length / 12) - 1} />
            <YAxis domain={[0, 100]} tick={TICK_STYLE} axisLine={AXIS_STYLE} tickLine={false} />
            <Tooltip
              contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
              formatter={(v, _, { payload }) => [v != null ? `${v} · ${tensionLabel(v)}` : '—', payload.title || 'Tension']}
            />
            <ReferenceLine y={55} stroke="hsl(var(--border))" strokeDasharray="4 2" label={{ value: 'rising', style: { fontSize: 9, fill: 'hsl(var(--muted-foreground))' } }} />
            <Bar dataKey="tension" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {tensionData.map((d) => (
                <Cell key={d.chapter} fill={tensionColor(d.tension)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Tab 4 — Continuity Timeline ───────────────────────────────────────────────
// Text-analysis scan: checks chapter content against character names and
// variable values for inconsistencies (misspellings, name variants).
// Runs on-demand via POST /api/projects/{pid}/arc/continuity-scan.

function ContinuityTab({ chapters, project }) {
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);

  const runScan = useCallback(async () => {
    if (!project?.id) return;
    setScanning(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${project.id}/arc/continuity-scan`,
        {},
        { withCredentials: true, timeout: 45000 },
      );
      setResults(data.chapters || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Continuity scan failed. Try again.');
    } finally {
      setScanning(false);
    }
  }, [project?.id]);

  if (!results && !scanning) {
    return (
      <EmptyState
        icon={GitBranch}
        title="Continuity Scanner"
        body="Scans every chapter's text against your character names and variables — flags chapters where a name appears with a different spelling."
        onGenerate={runScan}
        generating={scanning}
        label="Scan Chapters"
      />
    );
  }

  if (scanning) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-[13px]">Scanning {chapters.length} chapters…</p>
      </div>
    );
  }

  const totalIssues = results.reduce((s, r) => s + (r.issue_count || 0), 0);
  const avgScore    = results.length ? Math.round(results.reduce((s, r) => s + r.score, 0) / results.length) : 100;

  return (
    <div className="flex flex-1 flex-col overflow-hidden" data-testid="continuity-results">
      {/* Summary bar */}
      <div className="flex shrink-0 items-center gap-4 border-b border-panel-border px-4 py-2 text-[12px]">
        <span className="font-semibold text-foreground">
          Avg health: <span className={avgScore >= 80 ? 'text-emerald-500' : avgScore >= 60 ? 'text-amber-500' : 'text-destructive'}>{avgScore}/100</span>
        </span>
        <span className="text-muted-foreground">
          {totalIssues === 0 ? 'No issues found' : `${totalIssues} potential issue${totalIssues === 1 ? '' : 's'} across ${results.filter(r => r.issue_count > 0).length} chapter${results.filter(r => r.issue_count > 0).length === 1 ? '' : 's'}`}
        </span>
        <button
          onClick={runScan}
          disabled={scanning}
          data-testid="continuity-rescan-btn"
          className="ml-auto flex items-center gap-1.5 rounded px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <RefreshCw className="h-3 w-3" />
          Re-scan
        </button>
      </div>

      {/* Per-chapter list */}
      <div className="flex-1 overflow-y-auto divide-y divide-panel-border">
        {results.map((r) => {
          const scoreColor = r.score >= 80 ? 'bg-emerald-500' : r.score >= 60 ? 'bg-amber-500' : 'bg-destructive';
          return (
            <div key={r.chapter_number} className="px-4 py-2.5" data-testid={`continuity-chapter-${r.chapter_number}`}>
              <div className="flex items-center gap-3">
                <span className="w-12 shrink-0 text-[11px] font-bold text-muted-foreground">Ch {r.chapter_number}</span>
                {/* Health bar */}
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn('h-full rounded-full transition-all', scoreColor)}
                    style={{ width: `${r.score}%` }}
                  />
                </div>
                <span className={cn('w-8 text-right text-[11px] font-semibold tabular-nums', r.score >= 80 ? 'text-emerald-500' : r.score >= 60 ? 'text-amber-500' : 'text-destructive')}>
                  {r.score}
                </span>
              </div>
              {r.issue_count > 0 && (
                <div className="mt-1.5 space-y-1 pl-15">
                  {r.flags.map((flag, fi) => (
                    <p key={fi} className="text-[11px] leading-snug text-muted-foreground">
                      <span className="font-medium text-amber-500">'{flag.found}'</span>
                      {' '}may be a variant of{' '}
                      <span className="font-medium text-foreground">'{flag.expected}'</span>
                      {' '}({flag.count}× in this chapter)
                    </p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab 5 — Story Map ─────────────────────────────────────────────────────────
// Shows character arc lines overlaid on colored act-zone bands and beat markers
// pulled from project.plot_extraction.  Requires arc data (Score Presence first)
// and plot extraction (run Plot Builder first).

function StoryMapTab({ characters, chapters, project, setCharacters }) {
  const storageKey = project?.id ? `arc-storymap-ids-${project.id}` : null;

  const [activeIds, setActiveIds] = useState(() => {
    if (!storageKey) return [];
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(activeIds)); } catch { /* non-fatal */ }
  }, [activeIds, storageKey]);

  const plotExtraction = project?.plot_extraction;
  const structureBeats = plotExtraction?.structure_beats || [];
  const structureType  = plotExtraction?.structure_type  || '3-act';

  const hasPlotData = structureBeats.some(
    b => b.chapter != null && b.chapter !== '' && Number(b.chapter) > 0,
  );
  const hasArcData = characters.some(c => Array.isArray(c.arc) && c.arc.length > 0);

  const visibleCharacters = useMemo(
    () => characters.filter(c => activeIds.includes(c.id)),
    [characters, activeIds],
  );

  const data = useMemo(() =>
    chapters.map((ch, i) => {
      const row = { ch: `${ch.number}` };
      characters.forEach(ch2 => {
        const arc = Array.isArray(ch2.arc) ? ch2.arc : [];
        row[ch2.name] = typeof arc[i] === 'number' ? arc[i] : null;
      });
      return row;
    }),
    [characters, chapters],
  );

  // ── Act zone bands ───────────────────────────────────────────────────────
  const actZones = useMemo(() => {
    if (!hasPlotData || !chapters.length) return [];
    const beatsWithCh = structureBeats
      .filter(b => b.chapter != null && b.chapter !== '' && Number(b.chapter) > 0)
      .sort((a, b2) => Number(a.chapter) - Number(b2.chapter));
    if (!beatsWithCh.length) return [];

    const firstCh = chapters[0]?.number || 1;
    const lastCh  = (chapters[chapters.length - 1]?.number || chapters.length) + 1;

    if (structureType === '3-act') {
      const boundary1 = beatsWithCh.find(b => ['inciting_incident', 'first_plot_point'].includes(b.key));
      const boundary2 = beatsWithCh.find(b => ['climax', 'all_is_lost'].includes(b.key));
      const zones = [];
      if (boundary1 && Number(boundary1.chapter) > firstCh) {
        zones.push({ x1: `${firstCh}`, x2: `${boundary1.chapter}`, fill: '#38bdf8', label: 'Act I',   opacity: 0.09 });
      }
      if (boundary1 && boundary2 && Number(boundary1.chapter) < Number(boundary2.chapter)) {
        zones.push({ x1: `${boundary1.chapter}`, x2: `${boundary2.chapter}`, fill: '#fbbf24', label: 'Act II',  opacity: 0.09 });
      }
      if (boundary2) {
        zones.push({ x1: `${boundary2.chapter}`, x2: `${lastCh}`, fill: '#34d399', label: 'Act III', opacity: 0.09 });
      }
      return zones;
    }

    // Other structures: rotate band colors between each beat
    const ZONE_COLORS = ['#38bdf8', '#a78bfa', '#fbbf24', '#34d399', '#f87171', '#fb923c', '#4ade80'];
    return beatsWithCh.map((beat, i) => ({
      x1:      `${beat.chapter}`,
      x2:      `${beatsWithCh[i + 1]?.chapter || lastCh}`,
      fill:    ZONE_COLORS[i % ZONE_COLORS.length],
      label:   (beat.label || beat.key || '').split(' ').slice(0, 2).join(' '),
      opacity: 0.07,
    }));
  }, [hasPlotData, structureBeats, structureType, chapters]);

  // ── Beat markers (vertical lines) ────────────────────────────────────────
  const beatMarkers = useMemo(() => {
    if (!hasPlotData) return [];
    return structureBeats
      .filter(b => b.chapter != null && b.chapter !== '' && Number(b.chapter) > 0)
      .map(b => ({
        chapter:     `${b.chapter}`,
        label:       (b.label || b.key || '').split(' ').slice(0, 2).join(' '),
        description: b.description || '',
        key:         b.key,
      }));
  }, [hasPlotData, structureBeats]);

  const handleGenerate = useCallback(async () => {
    if (!project?.id) return;
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/projects/${project.id}/generate-arc-scores`,
        {},
        { withCredentials: true, timeout: 90000 },
      );
      const updated = r.data?.characters || [];
      if (updated.length) {
        setCharacters(prev => prev.map(c => {
          const found = updated.find(u => u.id === c.id);
          return found ? { ...c, arc: found.arc } : c;
        }));
        toast.success(`Arc scores generated for ${updated.length} character${updated.length === 1 ? '' : 's'}.`);
      } else {
        toast.warning('No arc data returned — make sure chapters have content.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Arc generation failed.');
    } finally {
      setGenerating(false);
    }
  }, [project?.id, setCharacters]);

  // ── Empty: no plot extraction ─────────────────────────────────────────────
  if (!hasPlotData) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <MapIcon className="h-10 w-10 text-muted-foreground/40" />
        <div>
          <p className="text-[13px] font-semibold">No plot structure yet</p>
          <p className="mt-1 max-w-[280px] text-[13.5px] leading-snug text-muted-foreground">
            Run Plot Builder to extract your story's turning points. Then come back here to see character arcs mapped to your structure.
          </p>
        </div>
        <Button
          variant="default"
          size="sm"
          className="gap-1.5"
          onClick={() => window.dispatchEvent(new CustomEvent('loom:open-plot-builder'))}
          data-testid="storymap-open-plotbuilder-btn"
        >
          <Zap className="h-3.5 w-3.5" />
          Open Plot Builder
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Character toggle bar */}
      <div className="flex flex-wrap items-center gap-1 border-b border-panel-border bg-panel-header/40 px-2 py-2">
        {characters.map((c, idx) => {
          const on    = activeIds.includes(c.id);
          const color = charColor(idx);
          return (
            <button
              key={c.id}
              onClick={() => setActiveIds(prev =>
                prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id],
              )}
              data-testid={`storymap-char-btn-${c.id}`}
              aria-pressed={on}
              className={cn(
                'flex items-center gap-1.5 rounded-sm border border-border bg-background px-1.5 py-1 text-[13px] transition-colors',
                on ? 'opacity-100' : 'opacity-30',
              )}
            >
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
              <span className="font-medium">{c.name.split(' ')[0]}</span>
            </button>
          );
        })}
        {!hasArcData && (
          <Button
            variant="outline"
            size="sm"
            className="ml-auto h-7 gap-1.5 px-2 text-[13px]"
            onClick={handleGenerate}
            disabled={generating || !project?.id || !characters.length}
            data-testid="storymap-score-btn"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {generating ? 'Scoring…' : 'Score Arcs'}
          </Button>
        )}
      </div>

      {/* Chart */}
      {hasArcData ? (
        <div className="flex-1 p-2 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={CHART_MARGIN}>
              {/* Coloured act-zone bands */}
              {actZones.map((zone, i) => (
                <ReferenceArea
                  key={i}
                  x1={zone.x1}
                  x2={zone.x2}
                  fill={zone.fill}
                  fillOpacity={zone.opacity}
                  stroke="none"
                  label={{
                    value:    zone.label,
                    position: 'insideTopLeft',
                    style:    { fontSize: 9, fill: zone.fill, fontWeight: 700, opacity: 0.85 },
                    offset:   6,
                  }}
                />
              ))}
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
              <XAxis dataKey="ch" tick={TICK_STYLE} axisLine={AXIS_STYLE} tickLine={false} label={X_LABEL_STYLE} />
              <YAxis domain={Y_DOMAIN} tick={TICK_STYLE} axisLine={AXIS_STYLE} tickLine={false} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                formatter={(v, name) => [v != null ? v : '—', name]}
              />
              <Legend wrapperStyle={{ fontSize: 10, paddingTop: 2 }} />
              <ReferenceLine y={50} stroke="hsl(var(--border))" strokeDasharray="3 3" />
              {/* Beat marker lines */}
              {beatMarkers.map((beat, i) => (
                <ReferenceLine
                  key={beat.key || i}
                  x={beat.chapter}
                  stroke="#f59e0b"
                  strokeWidth={1}
                  strokeDasharray="4 2"
                  label={{
                    value:    beat.label,
                    position: i % 2 === 0 ? 'insideTopLeft' : 'insideTopRight',
                    style:    { fontSize: 8, fill: '#f59e0b', fontWeight: 600 },
                    offset:   4,
                  }}
                />
              ))}
              {/* Character arc lines */}
              {visibleCharacters.map(c => (
                <Line
                  key={c.id}
                  type="monotone"
                  dataKey={c.name}
                  stroke={charColor(characters.indexOf(c))}
                  strokeWidth={2}
                  dot={DOT}
                  activeDot={ACTIVE_DOT}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyState
          icon={LineChartIcon}
          title="No arc data yet"
          body="Click Score Arcs to generate character presence data, then toggle characters above to map them to your story structure."
          onGenerate={handleGenerate}
          generating={generating}
          label="Score Arcs"
        />
      )}

      {/* Beat description strip */}
      {beatMarkers.filter(b => b.description).length > 0 && (
        <div
          className="shrink-0 max-h-28 overflow-y-auto border-t border-panel-border divide-y divide-panel-border/50"
          data-testid="storymap-beat-strip"
        >
          {beatMarkers.filter(b => b.description).map((beat, i) => (
            <div key={beat.key || i} className="flex items-start gap-2 px-3 py-1.5">
              <span
                className="mt-0.5 shrink-0 rounded-sm px-1 py-0.5 text-[9px] font-bold text-white"
                style={{ background: '#f59e0b', minWidth: 28, textAlign: 'center' }}
              >
                Ch {beat.chapter}
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold text-amber-500 truncate">{beat.label}</p>
                <p className="text-[11px] text-muted-foreground leading-tight line-clamp-2">{beat.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── Main panel ───────────────────────────────────────────────────────────────
export function ArcVisualizerPanel({ onClose, maximized, onMaximize }) {
  const { characters, chapters, project, setCharacters } = usePW();
  const [tab, setTab] = useState('overview');

  // Listen for external requests to switch to a specific tab
  // (e.g. after plot extraction → auto-jump to Story Map)
  useEffect(() => {
    const handler = (e) => {
      const target = e.detail;
      if (['overview', 'layers', 'tension', 'continuity', 'storymap'].includes(target)) {
        setTab(target);
      }
    };
    window.addEventListener('loom:arc-switch-tab', handler);
    return () => window.removeEventListener('loom:arc-switch-tab', handler);
  }, []);

  return (
    <PanelFrame title="Character Arc Visualizer" icon={LineChartIcon} onClose={onClose}
                onMaximize={onMaximize} maximized={maximized} testId="arc-panel"
                badge={`${chapters.length} ch`}>
      <div className="flex h-full flex-col">
        <TabBar active={tab} onChange={setTab} />
        {tab === 'overview' ? (
          <OverviewTab key="overview" characters={characters} chapters={chapters} project={project} setCharacters={setCharacters} />
        ) : tab === 'layers' ? (
          <LayersTab key="layers" characters={characters} chapters={chapters} project={project} setCharacters={setCharacters} />
        ) : tab === 'tension' ? (
          <TensionTab key="tension" chapters={chapters} project={project} />
        ) : tab === 'storymap' ? (
          <StoryMapTab key="storymap" characters={characters} chapters={chapters} project={project} setCharacters={setCharacters} />
        ) : (
          <ContinuityTab key="continuity" chapters={chapters} project={project} />
        )}
      </div>
    </PanelFrame>
  );
}
